import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { formatJalaliDate, parseJalaliDate, toGregorian } from '../utils/persian-date';
import { toEnglishNumbers } from '../utils/numbers';

export type UiLocale = 'fa' | 'en' | 'fr';
export type UiDirection = 'rtl' | 'ltr';

interface UiLocaleContextValue {
  locale: UiLocale;
  direction: UiDirection;
  culture: string;
  setLocale: (locale: UiLocale) => void;
  formatNumber: (value: number) => string;
  formatDate: (value: unknown) => string;
  formatDateTime: (value: unknown) => string;
}

const UiLocaleContext = createContext<UiLocaleContextValue | undefined>(undefined);

export function normalizeUiLocale(value?: string): UiLocale {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized.startsWith('fr')) return 'fr';
  if (normalized.startsWith('en')) return 'en';
  return 'fa';
}

/**
 * Reads the locale selected for the currently mounted DynamicForms runtime.
 * This non-hook helper is intentionally limited to utility code that cannot
 * consume React context (for example print and field-label helpers).
 */
export function readRuntimeUiLocale(): UiLocale {
  if (typeof document === 'undefined') return 'fa';
  const localizedRoot = document.querySelector<HTMLElement>('.df-runtime-localized-root[data-ui-locale]')
    ?? document.querySelector<HTMLElement>('[data-dynamicforms-react-root][data-ui-locale]');
  if (localizedRoot?.dataset.uiLocale) return normalizeUiLocale(localizedRoot.dataset.uiLocale);
  try {
    const stored = window.localStorage.getItem('df-admin-locale');
    if (stored === 'fa' || stored === 'en' || stored === 'fr') return stored;
  } catch { /* Local storage is optional. */ }
  const root = document.querySelector<HTMLElement>('[data-dynamicforms-react-root][data-culture]');
  return normalizeUiLocale(root?.dataset.culture || document.documentElement.lang);
}

function detectCulture(explicit?: string): string {
  if (explicit?.trim()) return explicit.trim();
  if (typeof document !== 'undefined') {
    const htmlLanguage = document.documentElement.lang;
    if (htmlLanguage?.trim()) return htmlLanguage.trim();
  }
  if (typeof navigator !== 'undefined' && navigator.language) return navigator.language;
  return 'fa-IR';
}

export function localeCulture(locale: UiLocale): string {
  if (locale === 'fr') return 'fr-FR';
  if (locale === 'en') return 'en-US';
  return 'fa-IR';
}

export function localeDirection(locale: UiLocale): UiDirection {
  return locale === 'fa' ? 'rtl' : 'ltr';
}

function persianDigits(value: string): string {
  return value.replace(/\d/g, (digit) => '۰۱۲۳۴۵۶۷۸۹'[Number(digit)] ?? digit);
}

function formatGregorianParts(jy: number, jm: number, jd: number, locale: UiLocale): string {
  const gregorian = toGregorian(jy, jm, jd);
  if (locale === 'fr') {
    return `${String(gregorian.gd).padStart(2, '0')}/${String(gregorian.gm).padStart(2, '0')}/${String(gregorian.gy).padStart(4, '0')}`;
  }
  return `${String(gregorian.gy).padStart(4, '0')}/${String(gregorian.gm).padStart(2, '0')}/${String(gregorian.gd).padStart(2, '0')}`;
}

function readMicrosoftDate(raw: string): Date | undefined {
  const match = /\/Date\((-?\d+)(?:[+-]\d+)?\)\//.exec(raw);
  if (!match) return undefined;
  const date = new Date(Number(match[1]));
  return Number.isNaN(date.valueOf()) ? undefined : date;
}

function formatCanonicalDate(rawValue: unknown, locale: UiLocale, includeTime: boolean): string {
  const raw = String(rawValue ?? '').trim();
  if (!raw) return '—';

  const normalized = toEnglishNumbers(raw);
  const parts = normalized.split(/\s+/);
  const dateText = parts[0] ?? '';
  const timeText = parts.length > 1 ? parts.slice(1).join(' ') : '';
  const jalali = parseJalaliDate(dateText);
  if (jalali) {
    const renderedDate = locale === 'fa'
      ? formatJalaliDate(jalali)
      : formatGregorianParts(jalali.jy, jalali.jm, jalali.jd, locale);
    const result = includeTime && timeText ? `${renderedDate} ${timeText}` : renderedDate;
    return locale === 'fa' ? persianDigits(result) : result;
  }

  const microsoftDate = readMicrosoftDate(raw);
  const date = microsoftDate ?? (() => {
    const parsed = new Date(raw);
    return Number.isNaN(parsed.valueOf()) ? undefined : parsed;
  })();
  if (date) {
    const options: Intl.DateTimeFormatOptions = includeTime
      ? { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }
      : { year: 'numeric', month: '2-digit', day: '2-digit' };
    if (locale === 'fa') options.timeZone = 'Asia/Tehran';
    return new Intl.DateTimeFormat(localeCulture(locale), options).format(date);
  }

  return locale === 'fa' ? persianDigits(raw) : raw;
}

export function UiLocaleProvider({ culture, persistKey, children }: { culture?: string; persistKey?: string; children: ReactNode }) {
  const detectedCulture = useMemo(() => detectCulture(culture), [culture]);
  const [locale, setLocaleState] = useState<UiLocale>(() => {
    if (persistKey && typeof window !== 'undefined') {
      try {
        const stored = window.localStorage.getItem(persistKey);
        if (stored === 'fa' || stored === 'en' || stored === 'fr') return stored;
      } catch { /* Local storage is optional. */ }
    }
    return normalizeUiLocale(detectedCulture);
  });

  useEffect(() => {
    if (!persistKey || typeof window === 'undefined') return;
    try { window.localStorage.setItem(persistKey, locale); } catch { /* Local storage is optional. */ }
  }, [locale, persistKey]);

  const setLocale = (next: UiLocale) => setLocaleState(next);
  const direction = localeDirection(locale);
  const resolvedCulture = localeCulture(locale);

  const value = useMemo<UiLocaleContextValue>(() => ({
    locale,
    direction,
    culture: resolvedCulture,
    setLocale,
    formatNumber: (number) => number.toLocaleString(resolvedCulture),
    formatDate: (date) => formatCanonicalDate(date, locale, false),
    formatDateTime: (date) => formatCanonicalDate(date, locale, true)
  }), [direction, locale, resolvedCulture]);

  return <UiLocaleContext.Provider value={value}>{children}</UiLocaleContext.Provider>;
}

export function useUiLocale(): UiLocaleContextValue {
  const context = useContext(UiLocaleContext);
  if (context) return context;
  const locale = normalizeUiLocale(detectCulture());
  const culture = localeCulture(locale);
  return {
    locale,
    direction: localeDirection(locale),
    culture,
    setLocale: () => undefined,
    formatNumber: (number) => number.toLocaleString(culture),
    formatDate: (date) => formatCanonicalDate(date, locale, false),
    formatDateTime: (date) => formatCanonicalDate(date, locale, true)
  };
}
